import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Factory, Store, CheckCircle2, XCircle, Send, PackageCheck, Ban, Printer } from 'lucide-react';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { jobWorkOrdersService } from '@/services/production';
import { printJobWorkChallan } from './jobWorkChallanPrint';
import { fmt } from '@/lib/format';
import type { JobWorkOrderStatus } from '@/types/production';

interface Props {
  jwoId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STATUS_TONE: Record<JobWorkOrderStatus, 'muted' | 'success' | 'warning' | 'destructive'> = {
  DRAFT: 'muted',
  APPROVED: 'warning',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
  REJECTED: 'destructive',
  CANCELLED: 'muted',
};

export function JobWorkOrderDetailSheet({ jwoId, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [challan, setChallan] = useState('');
  const [expectedReturn, setExpectedReturn] = useState('');
  const [ewayNo, setEwayNo] = useState('');
  const [ewayDate, setEwayDate] = useState('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [servicePo, setServicePo] = useState('');
  const [actualOutput, setActualOutput] = useState('');
  const [scrap, setScrap] = useState('');
  const [retEwayNo, setRetEwayNo] = useState('');
  const [retEwayDate, setRetEwayDate] = useState('');
  const [retVehicleNo, setRetVehicleNo] = useState('');
  const [printing, setPrinting] = useState(false);

  const q = useQuery({
    queryKey: ['production', 'jwo', jwoId],
    queryFn: () => jobWorkOrdersService.get(jwoId!),
    enabled: open && !!jwoId,
  });
  const jwo = q.data;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['production', 'jwo', jwoId] });
    qc.invalidateQueries({ queryKey: ['production', 'job-work-orders'] });
  };
  const approve = useMutation({
    mutationFn: () => jobWorkOrdersService.approve(jwoId!),
    onSuccess: () => { toast.success('Approved'); invalidate(); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Failed'),
  });
  const reject = useMutation({
    mutationFn: () => jobWorkOrdersService.reject(jwoId!),
    onSuccess: () => { toast.success('Rejected'); invalidate(); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Failed'),
  });
  const cancel = useMutation({
    mutationFn: () => jobWorkOrdersService.cancel(jwoId!),
    onSuccess: () => { toast.success('Cancelled'); invalidate(); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Failed'),
  });
  const issue = useMutation({
    mutationFn: () =>
      jobWorkOrdersService.issue(jwoId!, {
        challanNumber: challan.trim() || undefined,
        expectedReturnAt: expectedReturn || undefined,
        ewayBillNo: ewayNo.trim() || undefined,
        ewayBillDate: ewayDate || undefined,
        vehicleNo: vehicleNo.trim() || undefined,
        servicePoNo: servicePo.trim() || undefined,
      }),
    onSuccess: () => { toast.success('Materials issued — stock consumed'); invalidate(); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Failed'),
  });

  const printChallan = async (kind: 'out' | 'return') => {
    if (!jwoId) return;
    setPrinting(true);
    try {
      const data =
        kind === 'return'
          ? await jobWorkOrdersService.getReturnChallan(jwoId)
          : await jobWorkOrdersService.getChallan(jwoId);
      printJobWorkChallan(data);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to build challan');
    } finally {
      setPrinting(false);
    }
  };
  const receive = useMutation({
    mutationFn: () =>
      jobWorkOrdersService.receive(jwoId!, {
        actualOutputQty: actualOutput.trim() !== '' ? Number(actualOutput) : undefined,
        scrapQty: scrap.trim() !== '' ? Number(scrap) : undefined,
        returnEwayBillNo: retEwayNo.trim() || undefined,
        returnEwayBillDate: retEwayDate || undefined,
        returnVehicleNo: retVehicleNo.trim() || undefined,
      }),
    onSuccess: () => { toast.success('Output received — stock produced'); invalidate(); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Failed'),
  });

  const busy =
    approve.isPending || reject.isPending || cancel.isPending || issue.isPending || receive.isPending;

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      headerSlot={
        <div className="flex items-start gap-4">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15">
            {jwo?.mode === 'OUTSOURCED' ? <Store className="h-6 w-6" /> : <Factory className="h-6 w-6" />}
          </span>
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[12px] text-muted-foreground">{jwo?.jwoNumber}</span>
              {jwo && <Badge variant={STATUS_TONE[jwo.status]} className="text-[10px]">{jwo.status}</Badge>}
              {jwo && (
                <Badge variant="outline" className="gap-1 text-[10px]">
                  {jwo.mode === 'OUTSOURCED' ? 'Outsourced' : 'In-house'}
                </Badge>
              )}
              {jwo && (
                <Badge variant="outline" className="gap-1 text-[10px]">
                  {jwo.returnableType === 'NON_RETURNABLE' ? 'Non-returnable' : 'Returnable'}
                </Badge>
              )}
            </div>
            <div className="text-[16px] font-semibold leading-tight">{jwo?.stepName}</div>
            <div className="text-[12px] text-muted-foreground">
              {jwo ? `Produces ${fmt.number(Number(jwo.plannedOutputQty))} × ${jwo.outputItem.name}` : ''}
              {jwo?.vendor ? ` · ${jwo.vendor.name}` : ''}
              {jwo ? ` · ${jwo.warehouse.name}` : ''}
            </div>
          </div>
        </div>
      }
      title=""
      hideFooter
      size="2xl"
      onSubmit={(e) => e.preventDefault()}
    >
      {q.isLoading || !jwo ? (
        <div className="h-40 animate-pulse rounded-md bg-muted" />
      ) : (
        <div className="space-y-4">
          {/* Inputs / BOM */}
          <div className="rounded-xl border border-border/60 bg-muted/20 p-3.5">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Materials consumed
            </div>
            <div className="space-y-1.5">
              {jwo.inputs.map((inp) => (
                <div key={inp.id} className="flex items-center justify-between text-[12.5px]">
                  <span className="text-foreground">{inp.item.name}</span>
                  <span className="tabular-nums text-muted-foreground">
                    planned {fmt.number(Number(inp.plannedQty))}
                    {inp.actualQty != null ? ` · used ${fmt.number(Number(inp.actualQty))}` : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Output */}
          <div className="flex items-center justify-between rounded-xl border border-border/60 bg-card p-3.5 text-[12.5px]">
            <span className="font-medium text-foreground">Output · {jwo.outputItem.name}</span>
            <span className="tabular-nums text-muted-foreground">
              planned {fmt.number(Number(jwo.plannedOutputQty))}
              {jwo.actualOutputQty != null ? ` · produced ${fmt.number(Number(jwo.actualOutputQty))}` : ''}
              {Number(jwo.scrapQty) > 0 ? ` · scrap ${fmt.number(Number(jwo.scrapQty))}` : ''}
            </span>
          </div>

          {jwo.challanNumber && (
            <div className="text-[12px] text-muted-foreground">
              Challan <span className="font-mono text-foreground">{jwo.challanNumber}</span>
              {jwo.issuedAt ? ` · issued ${fmt.date(jwo.issuedAt)}` : ''}
              {jwo.expectedReturnAt ? ` · expected back ${fmt.date(jwo.expectedReturnAt)}` : ''}
            </div>
          )}
          {jwo.reviewerNote && (
            <div className="rounded-lg bg-destructive/5 px-3 py-2 text-[12px] text-destructive">
              {jwo.reviewerNote}
            </div>
          )}

          {jwo.mode === 'OUTSOURCED' && jwo.status !== 'DRAFT' && jwo.status !== 'REJECTED' && jwo.status !== 'CANCELLED' && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" disabled={printing} onClick={() => printChallan('out')}>
                <Printer className="h-4 w-4" /> {printing ? 'Preparing…' : 'Print issue challan (OUT)'}
              </Button>
              {jwo.returnableType !== 'NON_RETURNABLE' && jwo.status === 'COMPLETED' && (
                <Button size="sm" variant="outline" disabled={printing} onClick={() => printChallan('return')}>
                  <Printer className="h-4 w-4" /> {printing ? 'Preparing…' : 'Print return receipt (IN)'}
                </Button>
              )}
            </div>
          )}

          {/* Stage-specific actions */}
          {jwo.status === 'DRAFT' && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" disabled={busy} onClick={() => approve.mutate()}>
                <CheckCircle2 className="h-4 w-4" /> Approve
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => reject.mutate()}>
                <XCircle className="h-4 w-4" /> Reject
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => cancel.mutate()}>
                <Ban className="h-4 w-4" /> Cancel
              </Button>
            </div>
          )}

          {jwo.status === 'APPROVED' && (
            <div className="space-y-3 rounded-xl border border-border/60 bg-muted/20 p-3.5">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Issue materials {jwo.mode === 'OUTSOURCED' ? 'to contractor' : '(consume stock)'}
              </div>
              {jwo.mode === 'OUTSOURCED' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Challan no." htmlFor="jwo-challan">
                      <Input id="jwo-challan" value={challan} onChange={(e) => setChallan(e.target.value)} className="h-9" />
                    </FormField>
                    <FormField
                      label="Expected back"
                      htmlFor="jwo-return"
                      hint={jwo.returnableType === 'NON_RETURNABLE' ? 'non-returnable' : undefined}
                    >
                      <Input id="jwo-return" type="date" value={expectedReturn}
                        disabled={jwo.returnableType === 'NON_RETURNABLE'}
                        onChange={(e) => setExpectedReturn(e.target.value)} className="h-9" />
                    </FormField>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="E-way bill no." htmlFor="jwo-eway">
                      <Input id="jwo-eway" value={ewayNo} onChange={(e) => setEwayNo(e.target.value)} className="h-9" />
                    </FormField>
                    <FormField label="E-way bill date" htmlFor="jwo-eway-date">
                      <Input id="jwo-eway-date" type="date" value={ewayDate} onChange={(e) => setEwayDate(e.target.value)} className="h-9" />
                    </FormField>
                    <FormField label="Vehicle no." htmlFor="jwo-vehicle">
                      <Input id="jwo-vehicle" value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)} className="h-9" />
                    </FormField>
                    <FormField label="Service PO no." htmlFor="jwo-po">
                      <Input id="jwo-po" value={servicePo} onChange={(e) => setServicePo(e.target.value)} className="h-9" />
                    </FormField>
                  </div>
                </>
              )}
              <div className="flex gap-2">
                <Button size="sm" disabled={busy} onClick={() => issue.mutate()}>
                  <Send className="h-4 w-4" /> Issue & consume
                </Button>
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => cancel.mutate()}>
                  <Ban className="h-4 w-4" /> Cancel
                </Button>
              </div>
            </div>
          )}

          {jwo.status === 'IN_PROGRESS' && (
            <div className="space-y-3 rounded-xl border border-border/60 bg-muted/20 p-3.5">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Receive output (produce stock)
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Good output" htmlFor="jwo-output" hint={`planned ${fmt.number(Number(jwo.plannedOutputQty))}`}>
                  <Input id="jwo-output" type="number" min={0} step="0.001" value={actualOutput}
                    placeholder={String(jwo.plannedOutputQty)}
                    onChange={(e) => setActualOutput(e.target.value)} className="h-9 text-right tabular-nums" />
                </FormField>
                <FormField label="Scrap" htmlFor="jwo-scrap">
                  <Input id="jwo-scrap" type="number" min={0} step="0.001" value={scrap}
                    onChange={(e) => setScrap(e.target.value)} className="h-9 text-right tabular-nums" />
                </FormField>
              </div>
              {jwo.mode === 'OUTSOURCED' && jwo.returnableType !== 'NON_RETURNABLE' && (
                <div className="grid grid-cols-3 gap-3">
                  <FormField label="Return e-way no." htmlFor="jwo-ret-eway">
                    <Input id="jwo-ret-eway" value={retEwayNo} onChange={(e) => setRetEwayNo(e.target.value)} className="h-9" />
                  </FormField>
                  <FormField label="Return e-way date" htmlFor="jwo-ret-eway-date">
                    <Input id="jwo-ret-eway-date" type="date" value={retEwayDate} onChange={(e) => setRetEwayDate(e.target.value)} className="h-9" />
                  </FormField>
                  <FormField label="Return vehicle no." htmlFor="jwo-ret-vehicle">
                    <Input id="jwo-ret-vehicle" value={retVehicleNo} onChange={(e) => setRetVehicleNo(e.target.value)} className="h-9" />
                  </FormField>
                </div>
              )}
              <Button size="sm" disabled={busy} onClick={() => receive.mutate()}>
                <PackageCheck className="h-4 w-4" /> Receive & produce
              </Button>
            </div>
          )}
        </div>
      )}
    </FormSheet>
  );
}
